import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { CheckCircle, ErrorOutline, GavelOutlined, InfoOutlined, PublicOutlined } from '@mui/icons-material';
import { fmtChecked, INK, TAG_SX } from './DirectoryLayout';
import { brand, text } from '../../theme/colors';

const TEAL_DEEP = '#134e4a';

/**
 * Register matches for people who are NOT on Eqaya (owner, 2026-09-27): Eqaya
 * vouches only for the realtors and conveyancers it has verified itself. For
 * anyone else we show what the public register says, marked as not verified by
 * Eqaya, and we do not try to verify them.
 */
const NotVerifiedChip = () => (
  <Chip size="small" icon={<PublicOutlined sx={{ fontSize: 16 }} />} label="Public register · not verified by Eqaya"
    sx={{ bgcolor: '#f1f5f4', color: '#475569', border: '1px solid #cbd5e1', fontWeight: 600, borderRadius: '8px' }} />
);

// The EAC register's standing for an agency: green when Compliant, amber for anything else.
const StatusTag = ({ status }) => {
  const ok = /^compliant$/i.test(String(status || '').trim());
  return (
    <Chip size="small" label={status || 'Status unknown'}
      icon={ok ? <CheckCircle sx={{ fontSize: 16, '&&': { color: '#166534' } }} /> : <ErrorOutline sx={{ fontSize: 16, '&&': { color: '#9a3412' } }} />}
      sx={ok
        ? { ...TAG_SX, bgcolor: '#dcfce7', color: '#166534', borderColor: '#86efac' }
        : { ...TAG_SX, bgcolor: '#fff7ed', color: '#9a3412', borderColor: '#fdba74' }} />
  );
};

const Row = ({ icon, title, sub, chips = [], status }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.75, sm: 1.5 }} alignItems={{ sm: 'center' }} sx={{ p: 1.75, borderRadius: '10px', bgcolor: '#fff', border: '1px solid #b9cbc7' }}>
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
      {icon}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: INK }}>{title}</Typography>
        {sub && <Typography sx={{ fontSize: 14, color: text.secondary }}>{sub}</Typography>}
        {(chips.length > 0 || status !== undefined) && (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
            {chips.map((c) => <Chip key={c} size="small" label={c} sx={TAG_SX} />)}
            {status !== undefined && <StatusTag status={status} />}
          </Stack>
        )}
      </Box>
    </Stack>
    <NotVerifiedChip />
  </Stack>
);

/** What Eqaya does and does not vouch for — said once, the same way on both directories. */
function OnlyOurMembers({ kind, q }) {
  const who = kind === 'realtor' ? 'realtors' : 'conveyancers';
  const check = kind === 'realtor'
    ? 'we confirm with their agency, on the contact details the Estate Agents Council publishes, that they work there, and check the agency is Compliant'
    : 'we check them against the Law Society register';
  return (
    <Alert severity="info" icon={<InfoOutlined />} sx={{ mt: 2, borderRadius: '10px', '& .MuiAlert-message': { width: '100%' } }}>
      <Typography sx={{ fontWeight: 700, color: INK, mb: 0.5 }}>{q ? `Eqaya has not verified “${q}”` : 'Eqaya vouches only for its own members'}</Typography>
      <Typography sx={{ fontSize: 14.5, color: INK }}>
        Eqaya vouches only for {who} who have joined Eqaya and passed our checks: {check}. We do not verify people who have not joined.
        If you already deal with someone, ask them to join Eqaya. Once we have verified them, they appear here.
      </Typography>
      <Button component={RouterLink} to={kind === 'realtor' ? '/realtors' : '/conveyancers'}
        sx={{ mt: 1.5, borderRadius: '10px', textTransform: 'none', fontWeight: 700, bgcolor: brand.primaryDark, color: '#fff', '&:hover': { bgcolor: TEAL_DEEP } }}>
        See Eqaya’s verified {who}
      </Button>
    </Alert>
  );
}

/** Conveyancers from the Law Society directory who match the name, not on Eqaya. */
export function ConveyancerRegisterResults({ register, q }) {
  if (!register) return null;
  return (
    <Box component="section" sx={{ mt: 4 }}>
      <Typography component="h2" sx={{ fontSize: 20, fontWeight: 800, color: INK }}>On the Law Society register</Typography>
      <Typography sx={{ fontSize: 14.5, color: text.secondary, mb: 1.75 }}>
        {register.total
          ? `${register.total} conveyancer${register.total === 1 ? '' : 's'} matching “${q}” may practise this year, according to the Law Society directory (${fmtChecked(register.checkedAt)}). They have not joined Eqaya, so Eqaya has not verified them.`
          : `No conveyancer matching “${q}” is on the Law Society directory as allowed to transfer property this year.`}
      </Typography>
      <Stack spacing={1}>
        {register.items.map((c) => (
          <Row key={c.id} icon={<GavelOutlined sx={{ color: TEAL_DEEP, mt: '2px' }} />} title={c.name}
            sub={[c.firm, c.town].filter(Boolean).join(', ')}
            chips={['Conveyancer', ...(c.notary ? ['Notary public'] : []), ...(c.partner ? ['Partner'] : [])]} />
        ))}
      </Stack>
      {register.total > register.items.length && <Typography sx={{ fontSize: 13.5, color: text.secondary, mt: 1 }}>Showing {register.items.length} of {register.total}. Add a first name or pick a town to narrow it.</Typography>}
      <OnlyOurMembers kind="conveyancer" />
    </Box>
  );
}

/**
 * Realtors: the EAC register lists agencies, and names the principal agent on
 * some, so a person's name only matches a named principal. Either way these are
 * public entries, not people Eqaya has verified.
 */
export function RealtorRegisterResults({ register, q, memberCount = 0 }) {
  if (!register) return null;
  const principals = register.items.filter((a) => a.matchedOn === 'principal');
  const agencies = register.items.filter((a) => a.matchedOn !== 'principal');
  return (
    <Box component="section" sx={{ mt: 4 }}>
      <Typography component="h2" sx={{ fontSize: 20, fontWeight: 800, color: INK }}>On the Estate Agents Council register</Typography>
      <Typography sx={{ fontSize: 14.5, color: text.secondary, mb: 1.75 }}>
        The Council&apos;s public register lists agencies, and names the person in charge of only some of them. Individual agents are not listed, so a person&apos;s name usually finds nothing here.
      </Typography>
      <Stack spacing={1}>
        {principals.map((a) => (
          <Row key={`p${a.id}`} icon={<PublicOutlined sx={{ color: TEAL_DEEP, mt: '2px' }} />} title={a.principal}
            sub={`Principal agent, ${a.agency}${a.address ? ` · ${a.address}` : ''}`} status={a.status || null} />
        ))}
        {agencies.map((a) => (
          <Row key={`a${a.id}`} icon={<PublicOutlined sx={{ color: TEAL_DEEP, mt: '2px' }} />} title={a.agency}
            sub={[a.principal ? `Principal agent: ${a.principal}` : null, a.address].filter(Boolean).join(' · ')}
            chips={[a.individual ? 'Registered agent' : 'Agency']} status={a.status || null} />
        ))}
      </Stack>
      {register.items.length === 0 && <Typography sx={{ color: INK, fontWeight: 600 }}>Nothing on the register matches “{q}”.</Typography>}
      {memberCount === 0 && <OnlyOurMembers kind="realtor" q={q} />}
    </Box>
  );
}
